import { GetProductCodeByWebIdQuery } from 'packages/shared/definitions/types';

import { dispatchTrackingEvent } from '@context/tracking/utils';

import { Payload, ProductDetail } from './hooks/useTracking/model';

type LoadProductCodes = (
  ids: string[]
) => Promise<GetProductCodeByWebIdQuery['vpProductByIds']>;

const getTrackingProduct = ({
  product,
  listName,
  productCode,
}: {
  product: ProductDetail;
  productCode?: string | null;
  listName?: 'skin_anl_pdp' | 'skin_anl_list';
}) => ({
  brand_name: product.item_brand,
  full_price: product.price ?? 0,
  kind: '',
  price: product.price ?? 0,
  primary_subcategory: '',
  primary_category: product.item_category,
  primary_type: '',
  product_code: productCode ?? '',
  product_name: product.item_name,
  ...(listName && { list_name: listName }),
});

export const trackViewItem = async (
  product: ProductDetail,
  loadProductCodes: LoadProductCodes
) => {
  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
    listName: 'skin_anl_pdp',
  });

  dispatchTrackingEvent({
    event: 'view_item',
    products: [trackingProduct],
    location: 'skin_anl',
    _clear: true,
  });
};
export const trackViewItemList = async (
  products: ProductDetail[],
  loadProductCodes: LoadProductCodes
) => {
  const prodCode = await loadProductCodes(products.map((p) => p.item_id));

  const trackingProducts = products.filter(Boolean).map((product) => ({
    ...getTrackingProduct({
      product,
      productCode: prodCode.find((code) => code?.id === product.item_id)
        ?.productCode,
      listName: 'skin_anl_list',
    }),
    list_position: 0,
  }));

  dispatchTrackingEvent({
    event: 'view_item_list',
    product_lists: {
      list_name: 'skin_anl_list',
      interaction: 'click',
      sorting_type: 'automatic',
      products: trackingProducts,
    },
    _clear: true,
  });
};

export const trackAddToCard = async (
  product: ProductDetail,
  loadProductCodes: LoadProductCodes
) => {
  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
  });

  dispatchTrackingEvent({
    event: 'add_to_cart',
    product: {
      ...trackingProduct,
      quantity: 1,
      cart_type: 'skin_anl',
    },
    _clear: true,
  });
};

export const trackAddToWishList = async (
  product: ProductDetail,
  timing: number,
  loadProductCodes: LoadProductCodes
) => {
  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
  });

  dispatchTrackingEvent({
    event: 'add_to_wishlist',
    wishlist: {
      action: 'product_added_to_wishlist',
      interaction: 'click_add',
      type: 'skin_anl',
      timing,
      products: [trackingProduct],
    },
    _clear: true,
  });
};

export const trackRemoveFromWishList = async (
  product: ProductDetail,
  timing: number,
  loadProductCodes: LoadProductCodes
) => {
  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
  });

  dispatchTrackingEvent({
    event: 'remove_from_wishlist',
    wishlist: {
      action: 'product_removed_from_wishlist',
      interaction: 'click_add',
      type: 'skin_anl',
      timing,
      products: [trackingProduct],
    },
    _clear: true,
  });
};

export const trackViewOrSelectPromotion = (
  brand: string,
  event: 'select_promotion' | 'view_promotion'
) => {
  dispatchTrackingEvent({
    event,
    promotions: [
      {
        code: 'skin_anl',
        id: brand,
        location: 'skin_anl_pdp',
        creative: '',
        destination_url: '',
        name: '',
        position: 0,
      },
    ],
    _clear: true,
  });
};

export const extendSAPageViewTracking = (
  payload: Payload,
  brandId?: string
) => {
  if (payload.page_title !== 'Start' || !brandId) {
    return;
  }

  const isMobile = new URLSearchParams(window.location.search).get(
    'mobile-view'
  );

  payload.textValue = brandId;
  payload.isBrandSpecificSA = true;
  payload.isMobileApp = isMobile === 'true';
};
